// src/components/tabs/TabWorkspace.tsx
// The workspace that replaces the single page outlet: a split tree of tab
// groups. Also the bridge to the outside world — the address the browser is at,
// links opened from the sidebar, and the keyboard.

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { tabsStore } from '@/stores/TabsStore';
import { resolveTitle } from '@/features/tabs/logic';
import { PaneView } from './SplitView';

export const TabWorkspace = observer(() => {
  const location = useLocation();
  const { t } = useI18n();

  // The browser's location opens a tab: on first load it restores the deep
  // link, and afterwards it is how navigation from outside the panes — the
  // sidebar, search, a notification — arrives here.
  useEffect(() => {
    const path = location.pathname + location.search;
    const alreadyOpen = tabsStore.tabs.some((tb) => tb.path === path);
    if (alreadyOpen) {
      const tab = tabsStore.tabs.find((tb) => tb.path === path);
      if (tab && tabsStore.focusedGroup?.activeTabId !== tab.id) tabsStore.activateTab(tab.id);
      return;
    }
    tabsStore.openTab(path, resolveTitle(location.pathname, t));
  }, [location.pathname, location.search, t]);

  // Only the tab in front sets the document title, or every mounted page would
  // take turns overwriting it. Read during render so the observer re-runs the
  // effect when it changes — a store field is not a valid hook dependency.
  const activeTitle = tabsStore.activeTab?.title ?? '';
  useEffect(() => {
    if (activeTitle) document.title = activeTitle;
  }, [activeTitle]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const meta = e.metaKey || e.ctrlKey;
      if (!meta) return;
      const group = tabsStore.focusedGroup;
      if (!group) return;

      if (e.key === 'w' && !e.shiftKey) {
        const active = group.activeTabId;
        if (active) {
          e.preventDefault();
          tabsStore.close(active);
        }
        return;
      }
      // Cmd/Ctrl + \ splits, matching the IDE it is modelled on.
      if (e.key === '\\') {
        e.preventDefault();
        tabsStore.splitGroupById(group.id, e.shiftKey ? 'column' : 'row');
        return;
      }
      if (e.key === 'ArrowRight' && e.altKey) {
        const ids = group.tabIds;
        const at = ids.indexOf(group.activeTabId ?? '');
        if (at >= 0 && ids.length > 1) {
          e.preventDefault();
          tabsStore.activateTab(ids[(at + 1) % ids.length]);
        }
        return;
      }
      if (e.key === 'ArrowLeft' && e.altKey) {
        const ids = group.tabIds;
        const at = ids.indexOf(group.activeTabId ?? '');
        if (at >= 0 && ids.length > 1) {
          e.preventDefault();
          tabsStore.activateTab(ids[(at - 1 + ids.length) % ids.length]);
        }
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <div className="flex flex-1 min-w-0 min-h-0">
      <PaneView node={tabsStore.root} />
    </div>
  );
});
